// Relative path: src/components/Popup/Popup.js

import React, { useEffect, useState } from 'react';

/**
 * Base Popup component with responsive behavior:
 * - On mobile: slides in from the bottom of the screen
 * - On desktop: appears centered on the screen
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the popup is visible
 * @param {Function} props.onClose - Function to call when popup is closed
 * @param {React.ReactNode} props.children - Content to display inside the popup
 * @param {string} props.className - Additional CSS classes to apply to the popup content
 * @param {boolean} props.forceMobile - Force mobile style even on desktop (optional)
 * @param {string} props.theme - 'light' (default) or 'dark' for styling
 */
const Popup = ({ isOpen, onClose, children, className = '', forceMobile = false, theme = 'light' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  
  const isDark = theme === 'dark';
  const bgColor = isDark ? 'bg-black' : 'bg-white';
  const closeButtonBg = isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200';
  const closeButtonText = isDark ? 'text-gray-300' : 'text-gray-600';
  
  // Detect mobile/desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(forceMobile || window.innerWidth < 768); // 768px is Tailwind's md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [forceMobile]);
  
  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      // Prevent body scrolling when popup is open
      document.body.style.overflow = 'hidden';
      // Small delay to ensure the DOM has the popup in place before animating
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      // Restore scrolling
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  // Don't render anything if not open
  if (!isOpen) return null;

  // Handle clicking outside popup content
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  return (
    <div 
      className={`fixed inset-0 z-50 flex bg-black bg-opacity-50 transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${isMobile ? 'items-end' : 'items-center'} justify-center`}
      onClick={handleBackdropClick}
    >
      <div 
        className={`relative ${
          isMobile 
            ? `w-full ${bgColor} rounded-t-2xl transition-transform duration-300 ease-out ${
                isVisible ? 'translate-y-0' : 'translate-y-full'
              }`
            : `w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 max-w-4xl ${bgColor} rounded-2xl shadow-xl transition-all duration-300 ease-out ${
                isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
              }`
        } ${className}`}
        style={{ 
          maxHeight: isMobile ? '90vh' : '85vh', 
          overflowY: 'auto' 
        }}
        onClick={e => e.stopPropagation()} // Prevent clicks from closing popup
      >
        {/* Close button */}
        <button 
          className={`absolute top-4 right-4 z-10 p-2 rounded-full ${closeButtonBg} transition-colors`}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close popup"
        >
          <svg className={`w-5 h-5 ${closeButtonText}`} fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        
        {/* Popup content */}
        {children}
      </div>
    </div>
  );
};

export default Popup;